import { serviceOrigin } from './services';
// Realtime Voice Layer — Step 1 (narrator-only plus scoped companion modes).
// Design: Content post #1215, FE artifact p-83022cd0ae90.
//
// The narrator listens to the focused agent's live stream and speaks an ambient
// AgentOS-voice narration. In narrator-only mode it can do NOTHING but talk —
// narrator tokens are minted tool-less. Arcturian may receive explicitly
// owner-approved read tools; their calls return through the authenticated
// AiApi proxy below. Audio runs Browser↔OpenAI directly (WebRTC, lowest
// latency); the real OpenAI key never reaches the browser — AiApi mints a short-
// lived `ek_` client-secret via POST /ai/realtime/token.

const aiApi = () => serviceOrigin('ai');
const OPENAI_REALTIME = 'https://api.openai.com/v1/realtime/calls';

// 'agentos-narrator' (AiApi PR #62): the persistent continuous-flow AgentOS
// persona (3rd person, ambient) — read-only, tools=[] server-enforced, same as
// narrator-only but with the AgentOS voice + Context-Segment-Contract prompt.
// 'arcturian' (Product #4431 p-5cde1ac88a89, Web-Consumer-Lane freigegeben
// 2026-08-07): die persönliche operative Sprach-Ebene mit verpflichtendem
// Turn-Resolver und serverseitigem Action-Executor. Im deployten AiApi in
// SUPPORTED_COMPANION_MODES enthalten; der Mint liefert für diesen Modus den
// Vertrag mit, gegen den der Client VOR dem Verbinden validiert.
export type CompanionMode = 'narrator-only' | 'talkback-enabled' | 'agentos-narrator' | 'agent-transparent' | 'arcturian';

// OpenAI rejects a manual audio-buffer commit below 100 ms. WebRTC does not
// expose the provider-side PCM byte count, so the browser uses a deliberately
// conservative hold duration and clears accidental taps instead of carrying
// their partial audio into the next turn.
export const MIN_PTT_HOLD_MS = 250;

export function pttReleaseEvents(heldForMs: number): Array<{ type: string }> {
  return heldForMs >= MIN_PTT_HOLD_MS
    ? [{ type: 'input_audio_buffer.commit' }, { type: 'response.create' }]
    : [{ type: 'input_audio_buffer.clear' }];
}

export interface RealtimeToken {
  client_secret: string;
  model: string;
  /** Strukturierter Fehlerkörper des Mints (AiApi), roh durchgereicht — heute
   *  für die Budget-Auskunft (window/used_eur/limit_eur/resets_at). Ohne ihn
   *  kann die Anzeige nur den Code nennen, und dann rät sie (siehe
   *  `friendlyVoiceError`). */
  error_detail?: Record<string, unknown>;
  voice?: string;
  expires_at?: number;
  session_id?: string;
  // The session's tool names. Narrator modes keep this empty; Arcturian verifies
  // its explicit read-only capability list before connecting.
  tools?: unknown[];
  companion_mode?: string; // echoed; canonical read-only proof field
  companion_run_id?: string; // persists across 60-min rollovers (AiApi PR #62)
  detail_level?: string;
  // Echoed server truth for the opt-in PTT contract. Clients must verify both
  // fields before opening the microphone; the request alone proves nothing.
  push_to_talk?: boolean;
  turn_detection?: unknown | null;
  arcturian_resolver?: string;
  response_kind_field?: string;
  response_kinds?: {
    server_set?: unknown;
    adapter_set?: unknown;
    adapter_correlation_keys?: unknown;
  };
  resolver_followup?: RealtimeResponseCreateEvent;
  primary_audio_response?: RealtimeResponseCreateEvent;
  affect_followup?: RealtimeResponseCreateEvent;
  /** v3 + `read_tools`: erzwungener Nachschlag-Zug vor dem Sprechzug. */
  status_lookup_response?: RealtimeResponseCreateEvent;
  /** #1035: erzwungener Zug fuer die Absichtszeile nach einem Fehlschlag. */
  report_intent_response?: RealtimeResponseCreateEvent;
  conversation_id?: string;
  /** Server-selected Arcturian history items. Kept unknown until the dedicated
   *  continuity boundary validates the current provider wire fail-closed. */
  preface?: unknown;
  /** Compatibility boundary for the pre-Phase-2 replay contract. */
  prefaced_through_revision?: unknown;
  /** Browser-side diagnostics: the allowlisted AiApi origin that answered. */
  mint_endpoint?: string;
  /** Names only, never values. Keeps client_secret and other response secrets out of errors. */
  mint_response_keys?: string[];
  error?: string;
}

export interface RealtimeResponseCreateEvent {
  type: 'response.create';
  response: Record<string, unknown> & { metadata?: Record<string, unknown> };
}

function diagnosticValue(value: unknown): string {
  try {
    const rendered = JSON.stringify(value);
    return rendered === undefined ? String(value) : rendered;
  } catch {
    return String(value);
  }
}

/** Explain the manual-turn contract without weakening its fail-closed gate.
 * Missing fields mean an old/incomplete AiApi response; present-but-wrong
 * fields mean the server explicitly minted a contradictory contract. */
export function pttContractError(token: RealtimeToken): string | null {
  const hasPtt = Object.prototype.hasOwnProperty.call(token, 'push_to_talk');
  const hasTurnDetection = Object.prototype.hasOwnProperty.call(token, 'turn_detection');
  if (hasPtt && hasTurnDetection
      && token.push_to_talk === true && token.turn_detection === null) return null;

  const endpoint = token.mint_endpoint || 'unbekannter AiApi-Endpoint';
  const keys = token.mint_response_keys?.length
    ? token.mint_response_keys.join(', ')
    : 'nicht erfasst';
  const missing = [
    ...(!hasPtt ? ['push_to_talk'] : []),
    ...(!hasTurnDetection ? ['turn_detection'] : []),
  ];
  if (missing.length) {
    return `Push-to-Talk nicht verfügbar: ${endpoint} liefert einen veralteten oder unvollständigen Token-Vertrag; fehlend: ${missing.join(', ')}. Antwortschlüssel: ${keys}`;
  }
  return `Push-to-Talk-Token-Contract widerspricht der Anfrage bei ${endpoint}: push_to_talk=${diagnosticValue(token.push_to_talk)}, turn_detection=${diagnosticValue(token.turn_detection)} (erwartet true/null). Antwortschlüssel: ${keys}`;
}

/** Mint a short-lived ephemeral session token from AiApi (key stays server-side).
 *  `apiBase` MUST be the server-resolved `realtime_endpoint` from /auth/me
 *  (Alex→arkturian, Jascha→pdrei) — never hardcoded. The user JWT rides in the
 *  Authorization header (NEVER the body); AiApi exchanges it s2s for a grant. */
export async function mintToken(opts: {
  sessionId: string;
  mode: CompanionMode;
  apiBase?: string;
  voice?: string;
  language?: string;
  // Continuous-flow (AiApi PR #62): stable across the whole companion run incl.
  // 60-min rollovers; the per-transport voice_session_id changes on re-mint.
  companionRunId?: string;
  voiceSessionId?: string;
  // Narration verbosity (AiApi): brief = nur Status-Wendepunkte + Agent→Operator
  // Nachrichten (knapp + günstig); balanced = laufende Zusammenfassung; technical
  // = Tools/Diffs benannt. Frozen per session (Alex 2026-06-24: weniger Verbosität).
  detailLevel?: string;
  /** Opt into provider-side manual turn boundaries for a true PTT session. */
  pushToTalk?: boolean;
  /** Negotiated Arcturian resolver schema. Omitted for every other mode. */
  arcturianResolver?: string;
  /** Explicitly opt Arcturian into AiApi's owner-scoped read-tool contract. */
  readTools?: boolean;
  /** Existing owner-scoped Arcturian conversation to continue. Arcturian only. */
  conversationId?: string;
}): Promise<RealtimeToken> {
  const endpoint = opts.apiBase || aiApi();
  // Instanz deklariert die Sprachschicht mit null → gar nicht erst anrufen.
  // Die UI blendet den Einstieg über useServiceAvailable('ai') ohnehin aus;
  // das hier fängt nur Rennen (Mint vor geladener Registry-Map).
  if (!endpoint) return { client_secret: '', model: '', error: 'service_unavailable' };
  try {
    const token = bearerToken();
    const res = await fetch(`${endpoint}/ai/realtime/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({
        provider: 'openai',
        model: 'gpt-realtime',
        voice: opts.voice || 'marin',
        session_id: opts.sessionId,
        language: opts.language || 'de',
        companion_mode: opts.mode,
        ...(opts.companionRunId ? { companion_run_id: opts.companionRunId } : {}),
        ...(opts.voiceSessionId ? { voice_session_id: opts.voiceSessionId } : {}),
        ...(opts.detailLevel ? { detail_level: opts.detailLevel } : {}),
        // Omitted rather than `false`: older AiApi deployments ignore unknown
        // fields, and non-PTT callers keep their historical mint contract.
        ...(opts.pushToTalk ? { push_to_talk: true } : {}),
        ...(opts.arcturianResolver ? { arcturian_resolver: opts.arcturianResolver } : {}),
        ...(opts.mode === 'arcturian' && opts.readTools ? { read_tools: true } : {}),
        ...(opts.mode === 'arcturian' && opts.conversationId?.trim()
          ? { conversation_id: opts.conversationId.trim() }
          : {}),
        confirm_api_billing: true,
      }),
    });
    if (!res.ok) {
      let msg = `token ${res.status}`;
      let detail: Record<string, unknown> | undefined;
      try {
        const j = await res.json();
        msg = j?.detail?.error || j?.error || msg;
        // Das ganze Detail behalten, nicht nur den Code: Erst damit kann die
        // Anzeige sagen, WOVON die Grenze erreicht ist und WANN sie wieder
        // aufgeht (AiApi-Vertrag budget_exceeded: window/used_eur/limit_eur/
        // resets_at). Ohne diese Felder bleibt nur das nackte Stichwort.
        const d = (j && typeof j === 'object' ? (j as { detail?: unknown }).detail : undefined);
        if (d && typeof d === 'object') detail = d as Record<string, unknown>;
      } catch { /* keep status */ }
      return { client_secret: '', model: '', mint_endpoint: endpoint, error: msg, error_detail: detail };
    }
    const body = await res.json() as RealtimeToken;
    return {
      ...body,
      mint_endpoint: endpoint,
      // Key names are sufficient to spot a misplaced/old response contract.
      // Never put the raw body into a user-visible error: it contains ek_*.
      mint_response_keys: Object.keys(body).sort(),
    };
  } catch (e) {
    return {
      client_secret: '', model: '', mint_endpoint: endpoint,
      error: e instanceof Error ? e.message : 'token network error',
    };
  }
}

/** Route one model-issued read tool through AiApi under the operator's Bearer.
 * The browser never calls Federation services with an elevated identity. A
 * structured error is returned to the model so the spoken turn can stay honest
 * instead of hanging when the proxy or caller identity is unavailable. */
export async function callRealtimeReadTool(opts: {
  tool: string;
  arguments: Record<string, unknown>;
  callId: string;
  sessionId: string;
  apiBase?: string;
}): Promise<Record<string, unknown>> {
  const token = bearerToken();
  if (!token) {
    return {
      call_id: opts.callId,
      tool: opts.tool,
      ok: false,
      error: 'no_caller_identity',
    };
  }
  const toolBase = opts.apiBase || aiApi();
  if (!toolBase) {
    return { call_id: opts.callId, tool: opts.tool, ok: false, error: 'service_unavailable' };
  }
  try {
    const res = await fetch(`${toolBase}/ai/realtime/tool/${encodeURIComponent(opts.tool)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Session-ID': opts.sessionId,
      },
      body: JSON.stringify({ arguments: opts.arguments, call_id: opts.callId }),
    });
    if (!res.ok) {
      return {
        call_id: opts.callId,
        tool: opts.tool,
        ok: false,
        error: `tool_proxy_http_${res.status}`,
      };
    }
    const body = await res.json();
    return body && typeof body === 'object'
      ? body as Record<string, unknown>
      : { call_id: opts.callId, tool: opts.tool, ok: false, error: 'invalid_tool_response' };
  } catch {
    return {
      call_id: opts.callId,
      tool: opts.tool,
      ok: false,
      error: 'tool_proxy_unreachable',
    };
  }
}

// Redaction boundary (Codex code-review 2026-06-23): the agent's raw output
// MUST be sanitized before it leaves the browser to OpenAI — even in `balanced`.
// Strip secrets/keys/tokens and hard-cap length per feed event.
const MAX_FEED_CHARS = 2000;
const SECRET_PATTERNS: RegExp[] = [
  /\b[A-Za-z0-9_]*(?:SECRET|TOKEN|PASSWORD|PASSWD|API[_-]?KEY|PRIVATE[_-]?KEY|ACCESS[_-]?KEY)[A-Za-z0-9_]*\s*[=:]\s*\S+/gi, // env-style KEY=secret
  /\b(?:sk|ek|pk|rk|sk-proj)-[A-Za-z0-9_-]{12,}/g, // OpenAI / provider keys
  /\bBearer\s+[A-Za-z0-9._-]{12,}/gi,              // bearer tokens
  /\beyJ[A-Za-z0-9._-]{20,}/g,                     // JWTs
  /\b(?:AKIA|ASIA)[A-Z0-9]{12,}\b/g,              // AWS access key ids
  /\bghp_[A-Za-z0-9]{20,}/g,                       // GitHub PATs
  /-----BEGIN [A-Z ]*PRIVATE KEY-----[\s\S]*?-----END [A-Z ]*PRIVATE KEY-----/g, // PEM blocks
];

export function sanitizeForFeed(text: string): string {
  let out = text;
  for (const re of SECRET_PATTERNS) out = out.replace(re, '[redacted]');
  if (out.length > MAX_FEED_CHARS) out = out.slice(0, MAX_FEED_CHARS) + '\n…[gekürzt]';
  return out;
}

// Map AiApi's closed error-enum (PR #68) to user-friendly German messages.
// Unknown codes pass through unchanged (so already-friendly errors survive).
export interface VoiceErrorDetail {
  window?: unknown;      // 'daily' | 'monthly'
  used_eur?: unknown;
  limit_eur?: unknown;
  resets_at?: unknown;   // ISO 8601
}

/** „am 1. September" statt eines Zeitstempels — die Auskunft soll man lesen
 *  können, ohne sie zu übersetzen. Unparsbar → null, dann schweigt die Anzeige
 *  über den Zeitpunkt, statt einen falschen zu nennen. */
function budgetResetPhrase(iso: unknown): string | null {
  if (typeof iso !== 'string' || !iso.trim()) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  const d = new Date(t);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString('de-DE', {
    day: 'numeric', month: 'long', ...(sameYear ? {} : { year: 'numeric' }),
  });
}

function eur(v: unknown): string | null {
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return null;
  return n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

/**
 * Budget-Meldung aus dem Fehlerkörper — der Fall, der Alex fünf Tage gekostet
 * hat (18.08.): Der Dienst schickte `daily_budget_exceeded`, das Fenster war
 * aber MONATLICH (30 € Grenze, 32,21 € verbraucht, offen erst am 1.9.). Die
 * Anzeige versprach jeden Morgen „morgen wieder verfügbar" — eine Zusage, die
 * niemand einhalten konnte, weil der Name des Fehlercodes das Fenster nicht
 * kennt.
 *
 * Daraus die Regel hier: **Zeitpunkt nur nennen, wenn er im Fehler steht.**
 * Ohne `resets_at` sagt die Anzeige, dass gesperrt ist — nicht, bis wann.
 */
function budgetMessage(detail?: VoiceErrorDetail): string {
  const monthly = detail?.window === 'monthly';
  const head = monthly ? 'Monatslimit erreicht' : detail?.window === 'daily' ? 'Tageslimit erreicht' : 'Budget erreicht';
  const used = eur(detail?.used_eur);
  const limit = eur(detail?.limit_eur);
  const amount = used && limit ? ` (${used} von ${limit})` : '';
  const when = budgetResetPhrase(detail?.resets_at);
  return when ? `${head}${amount} — wieder verfügbar am ${when}.` : `${head}${amount} — die Sprachfunktion bleibt bis zum nächsten Abrechnungsfenster gesperrt.`;
}

export function friendlyVoiceError(code: string, detail?: VoiceErrorDetail): string {
  if (code === 'budget_exceeded' || code === 'daily_budget_exceeded' || code === 'monthly_budget_exceeded') {
    // Alt-Code `daily_…` ohne Detail: KEIN „morgen" versprechen. Der Name des
    // Codes ist kein Beleg für das Fenster — genau daran ist die alte Fassung
    // gescheitert.
    return budgetMessage(code === 'monthly_budget_exceeded' ? { window: 'monthly', ...detail } : detail);
  }
  const map: Record<string, string> = {
    realtime_not_enabled: 'Voice ist für deinen Account nicht aktiviert.',
    max_parallel_exceeded: 'Du hast bereits eine aktive Voice-Session laufen.',
    auth_down: 'Service kurzfristig nicht erreichbar, bitte später versuchen.',
    realtime_grant_expired: 'Sitzung abgelaufen — bitte erneut starten.',
    realtime_grant_invalid: 'Sitzung ungültig — bitte erneut starten.',
    realtime_scope_missing: 'Diese Funktion ist nicht freigeschaltet.',
    realtime_profile_misconfigured: 'Voice-Profil für deinen Account ist nicht konfiguriert.',
    realtime_wrong_profile: 'Falscher Voice-Host für deinen Account.',
  };
  return map[code] || code;
}

/** Exchange the local SDP offer for OpenAI's answer using the ephemeral secret. */
export async function openaiSdpExchange(model: string, clientSecret: string, offerSdp: string): Promise<string> {
  const res = await fetch(`${OPENAI_REALTIME}?model=${encodeURIComponent(model)}`, {
    method: 'POST',
    body: offerSdp,
    headers: { Authorization: `Bearer ${clientSecret}`, 'Content-Type': 'application/sdp' },
  });
  if (!res.ok) throw new Error(`OpenAI SDP ${res.status}: ${await res.text().catch(() => '')}`);
  return res.text();
}

// Usage record — POSTed exactly ONCE per `response.done`, NO auto-retry until
// AiApi ships server-side dedup on (voice_session_id, usage_event_id) (Codex'
// hard contract). `keepalive:true` lets an in-flight POST finish even if the
// tab closes — so we do NOT also sendBeacon: re-sending an already-attempted
// record would be a retry with double-count risk (Codex 20:03). A failed/
// uncertain POST stays `unsent_or_unknown` (lower-bound marker for the report),
// never re-sent.
export interface UsageRecord {
  voice_session_id: string;
  usage_event_id: string; // = OpenAI response.id (stable; drop-in for future dedup)
  audio_input_tokens?: number;
  audio_output_tokens?: number;
  text_input_tokens?: number;
  text_output_tokens?: number;
}

// Returns the ACK shape so a pending-queue can clear a record on accepted|deduped
// (Codex' rule). The no-retry Step-1 path only needs `ok`.
export interface UsageAck { ok: boolean; accepted?: boolean; deduped?: boolean }

// Dev-only narration-log upload (Alex 2026-06-24): in developer mode the client
// auto-uploads the full session transcript (feed ↔ narration ↔ operator, with
// ts/kind/agent/epoch/seg) so CloudV2 can analyse what reached the narrator and
// how it behaved — no manual export. Owner-scoped via the user JWT, upsert by
// voice_session_id, toggleable (off by default). MUST be switched off again
// after the analysis window.
// Bearer resolution is INJECTABLE so this module is auth-agnostic and portable
// across federation hosts (Realtime-Guide work 2026-06-25, post #1233): AgentOS
// reads `ark-auth` from localStorage by default; the Wanderlaut/audio-guide app
// injects its own `getToken()` (`wanderlaut-access-token`, same auth-api issuer)
// via setBearerTokenResolver. Same-issuer JWT → AiApi's grant-exchange accepts
// either 1:1, no token-exchange. Default preserves all existing AgentOS callers.
const defaultBearerResolver = (): string | null => {
  try {
    const raw = localStorage.getItem('ark-auth');
    return raw ? (JSON.parse(raw)?.access_token || null) : null;
  } catch { return null; }
};
let bearerResolver: () => string | null = defaultBearerResolver;

/** Override how this module obtains the user JWT (e.g. Wanderlaut's getToken()).
 *  Call once at app init; pass nothing to restore the AgentOS localStorage default. */
export function setBearerTokenResolver(fn?: (() => string | null) | null): void {
  bearerResolver = fn || defaultBearerResolver;
}

function bearerToken(): string | null {
  return bearerResolver();
}

export interface DevLogPayload {
  voice_session_id: string;
  agent: string | null;
  started_at: number;
  ended_at: number | null;
  lines: unknown[];
}

export async function postDevLog(payload: DevLogPayload, apiBase?: string): Promise<boolean> {
  try {
    const token = bearerToken();
    const base = apiBase || aiApi();
    if (!base) return false;
    const res = await fetch(`${base}/ai/realtime/devlog`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(payload),
      keepalive: true,
    });
    return res.ok;
  } catch {
    return false;
  }
}

// Release the server-side parallel-session reservation on stop (AiApi PR #70) so
// a device switch works immediately instead of waiting for the orphan-reap TTL.
// `voiceSessionId` MUST be the same reservation key used at mint — i.e. the
// `session_id` we sent (AiApi keys on session_id || companion_run_id). Idempotent,
// owner-scoped server-side; best-effort + keepalive so it survives unmount.
export async function endSession(voiceSessionId: string, apiBase?: string): Promise<boolean> {
  try {
    const token = bearerToken();
    const base = apiBase || aiApi();
    if (!base) return false;
    const res = await fetch(`${base}/ai/realtime/session/end`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ voice_session_id: voiceSessionId }),
      keepalive: true,
    });
    return res.ok;
  } catch {
    return false;
  }
}

// Liveness heartbeat (AiApi PR #71) — POSTed every ~30s while a session is live
// so the server's short reap (REALTIME_REAP_SECONDS=90) frees a phantom slot fast
// instead of waiting 60 min / a clean /session/end. Same reservation key as mint.
// Returns: true = alive, false = slot is GONE (reaped/released elsewhere → FE must
// stop locally), null = transient (network/401) → do nothing.
export async function heartbeat(voiceSessionId: string, apiBase?: string): Promise<boolean | null> {
  try {
    const token = bearerToken();
    const base = apiBase || aiApi();
    if (!base) return null;
    const res = await fetch(`${base}/ai/realtime/session/heartbeat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ voice_session_id: voiceSessionId }),
    });
    if (!res.ok) return null;
    const body = await res.json().catch(() => ({}));
    return body?.alive === false ? false : true;
  } catch {
    return null;
  }
}

export async function reportUsage(rec: UsageRecord, apiBase?: string): Promise<UsageAck> {
  try {
    const usageToken = bearerToken();
    const base = apiBase || aiApi();
    if (!base) return { ok: false };
    const res = await fetch(`${base}/ai/realtime/usage`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(usageToken ? { Authorization: `Bearer ${usageToken}` } : {}) },
      // `model` + `duration_sec` per AiApi's contract (pricing); the idempotency
      // keys (voice_session_id, usage_event_id) ride along in `rec`.
      body: JSON.stringify({ model: 'gpt-realtime', duration_sec: 0, ...rec }),
      keepalive: true,
    });
    if (!res.ok) return { ok: false };
    const body = await res.json().catch(() => ({}));
    return { ok: true, accepted: body?.accepted, deduped: body?.deduped };
  } catch {
    return { ok: false };
  }
}
